<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset}" ref="observer">
    <artisio-modal size="lg" :visible="modal.show" :data="model" :title="modalTitle" @show="onShow"
                   @hidden="onHidden" :content-in-form="false" @ok.prevent="handleSubmit(onSubmit)"
                   :show-confirmation="showConfirmation" :ok-title="$t('Submit')" :is-loading="loading"
                   :disable-submit-btn="disableSubmitBtn" scrollable>
      <b-form @submit.prevent="onSubmit">
        <div class="row">
          <div class="col-md-4">
            <template v-if="customer">
              <b-form-group>
                <label>{{ $t('Customer') }}</label>
                <b-input :value="`${customer.display_name} - ${customer.email}`" disabled/>
              </b-form-group>
            </template>
            <template v-else>
              <customer-select-input-widget :model="model" attribute="customer_uuid" @select="onCustomerSelect"
                                            :expand="'billing_address.country' + (($hasPermission('change_customerbankaccount') && hasStripeProvider) ? ',term' : '')"/>
            </template>
          </div>
          <div class="col-md-4">
            <input-widget :model="model" attribute="date" type="datepicker" :min="$getMonthAgo(model.created_at)"
                          :max="$getMonthCurrent(model.created_at)"
                          :rules="`min_date:${$getMonthAgo(model.created_at)}|max_date:${$getMonthCurrent(model.created_at)}|required`"/>
          </div>
          <div class="col-md-4">
            <sale-select-input-widget @select="onSaleSelect($event)" :model="model.deposit_reservations[0]"
                                      attribute="auction_uuid"
                                      value-field="uuid"/>
          </div>
          <div class="col-md-4">
            <currency-input-widget
              :currency="depositReservationCurrency"
              :model="model.deposit_reservations[0]" attribute="amount" type="number" min="0"/>
          </div>
          <div class="col-sm-4" v-if="hasMoreThanOneLanguage">
            <language-code-select-input-widget hint :model="model" attribute="preferred_language_code"/>
          </div>
        </div>

        <div class="row">
          <div class="col-md-4">
            <div class="card">
              <div class="card-header d-flex align-items-center p-2">
                <div>
                  {{ $t('Customer Info') }}
                </div>
              </div>
              <div class="card-body p-2" style="min-height: 5.3em">
                <div v-if="customer || model.customer">
                  <customer-email-address-info :customer="customer ? customer : model.customer"
                                               :show-address="false"/>
                </div>
                <no-data-available v-else v-show="true" height="4"/>
              </div>
            </div>
          </div>
          <div class="col-md-4">
            <div class="card">
              <div class="card-header d-flex align-items-center p-2">
                <div>
                  {{ $t('Invoice Billing address') }}
                </div>
                <b-button class="ml-auto p-0" variant="link" v-permission="'change_invoice'"
                          @click="onBillingAddressEditClick">
                  <font-awesome-icon :icon="'pen'" style="font-size: .9em"></font-awesome-icon>
                  {{ $t('Edit') }}
                </b-button>
              </div>


              <div class="card-body p-2" style="min-height: 5.3em">
                <div v-if="model.invoice_billing_address">
                  <p v-html="$options.filters.asAddress(model.invoice_billing_address, false)" class="p-0"></p>
                </div>
                <no-data-available v-else v-show="true" height="4"/>
              </div>
            </div>
          </div>
        </div>


        <div class="row mt-4">
          <div class="col-md-4 d-flex">
            <h5>{{ $t('Comment') }}</h5>
          </div>
          <div class="col-md-12">
            <input-widget :model="model" attribute="comment" type="textarea" label=""/>
          </div>
        </div>
      </b-form>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import InvoicesService from "./InvoicesService";
  import {ON_PDF_MODAL_OPEN, ON_PDF_MODAL_DATA_RETRIEVE, ON_PDF_MODAL_CLOSE} from "@/plugins/event-bus/event-bus-names";
  import {SOURCE_INVOICES_TABLE} from "../constants";
  import NoDataAvailable from "../../core/components/common/NoDataAvailable";
  import CurrencyInputWidget from "../../core/widgets/CurrencyInputWidget";
  import CustomerSelectInputWidget from "@/core/widgets/CustomerSelectInputWidget";
  import DepositInvoiceModel from "@/modules/invoices/models/DepositInvoice.model";
  import DepositReservationModel from "@/modules/invoices/models/DepositReservation.model";
  import SaleSelectInputWidget from "@/core/widgets/SaleSelectInputWidget";
  import SalesService from "@/modules/sales/SalesService";
  import _ from 'lodash';
  import LanguageCodeSelectInputWidget from "@/core/widgets/LanguageCodeSelectInputWidget";
  import CustomerEmailAddressInfo from "../../core/components/customer-info/CustomerEmailAddressInfo";
  import {InvoiceModalMixin} from "@/modules/invoices/modals/invoiceModal.mixin";
  import uuidV4 from 'uuid';
  import {hasPermission} from "@/core/directives/permission";

  const {mapState, mapActions} = createNamespacedHelpers('invoices');
  const {mapState: mapStateGlobal, mapActions: globalMapActions} = createNamespacedHelpers('global');

  export default {
    name: "DepositInvoiceModal",
    mixins: [InvoiceModalMixin],
    components: {
      LanguageCodeSelectInputWidget,
      SaleSelectInputWidget,
      CustomerSelectInputWidget,
      CustomerEmailAddressInfo,
      CurrencyInputWidget,
      NoDataAvailable,
      InputWidget,
      ArtisioModal
    },
    data() {
      return {
        modalUuid: uuidV4(),
        customerUuid: null,
        loading: false,
        showConfirmation: true,
        model: new DepositInvoiceModel(),
      }
    },
    computed: {
      ...mapStateGlobal(['hasMoreThanOneLanguage', 'hasStripeProvider']),
      ...mapState({
        modal: state => state.depositInvoiceModal,
        customer: state => state.depositInvoiceModal.customer,
        editObject: state => state.depositInvoiceModal.object,
      }),
      disableSubmitBtn() {
        return !this.model.customer_uuid;
      },
      modalTitle() {
        return this.model.uuid ? this.$i18n.t('Update Deposit Invoice') : this.$i18n.t('Create Deposit Invoice')
      },
      depositReservationCurrency() {
        if (this.model && this.model.currency) {
          return this.model.currency.symbol;
        }
        try {
          return this.model.deposit_reservations[0].auction.currency.symbol
        } catch (e) {
          return null;
        }
      },
    },
    watch: {
      async customer() {
        this.$nextTick(function () {
          if (this.customer) {
            this.model.customer_uuid = this.customer.uuid;
            this.setDefaultAddresses(this.customer.billing_address, this.customer.shipping_address)
            this.customerUuid = this.customer.uuid
          }
        })
      },
      async editObject() {
        if (this.editObject && this.editObject.uuid) {
          let depositInvoice = _.cloneDeep(this.editObject)

          if (this.editObject.deposit_reservations && this.editObject.deposit_reservations.length > 0) {
            let promises = []
            depositInvoice.deposit_reservations.forEach(item => promises.push(SalesService.saleExists(item.sale_no, 'currency')));
            let sales = (await Promise.all(promises)).map(result => result.body);
            depositInvoice.deposit_reservations = depositInvoice.deposit_reservations.map(item => {
              return new DepositReservationModel({
                ...item,
                auction: sales.find(sale => sale.sale_no === item.sale_no)
              })
            })
          }

          this.customerUuid = depositInvoice.customer_uuid
          this.setDefaultAddresses(
            this.editObject.invoice_billing_address,
            null,
          )

          this.model = new DepositInvoiceModel(depositInvoice);
        }
      }
    },
    methods: {
      ...mapActions(['hideDepositInvoiceModal', 'getTableData']),
      ...globalMapActions(['showAddressModal']),
      async onShow() {
        this.showConfirmation = true;
        this.loading = false;
      },
      onHidden() {
        this.model = new DepositInvoiceModel();
        this.customerUuid = null;
        this.hideDepositInvoiceModal();
      },
      async onSubmit() {
        if (this.model.is_not_editable) {
          this.$errorDialog(this.$i18n.t('You can not edit legacy invoice'));
          return;
        }
        this.model.resetErrors();

        this.loading = true;
        let response;

        this.model.type = 'deposit';

        if (this.model.deposit_reservations.length > 0) {
          this.model.deposit_amount = this.model.deposit_reservations.reduce((a, b) => a + parseFloat(b.amount || 0), 0);
        }

        const payload = {
          ...this.model,
          invoice_billing_address: this.model.invoice_billing_address,
        }
        if (!this.model.deposit_reservations[0].auction_uuid) {
          payload.deposit_reservations = []
        }

        if (hasPermission('change_customerbankaccount') && this.hasStripeProvider) {
          payload['exclude_from_auto_payments'] = this.model.exclude_from_auto_payments;
        }

        if (!payload.uuid) {
          response = await InvoicesService.createInvoice(payload);
        } else {
          response = await InvoicesService.updateInvoice(payload.uuid, payload);
        }
        this.loading = false;

        if (response.success) {
          this.showConfirmation = false;

          if (this.modal.source === SOURCE_INVOICES_TABLE) {
            this.getTableData();
          }

          if (!this.model.uuid && this.$hasPermission('export_invoice')) {
            const invoice = response.body.invoice;
            this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
              title: this.$i18n.t(`Invoice {invoice_no}`, {invoice_no: invoice.invoice_no}),
            });
            let pdfDataResponse = await InvoicesService.getInvoicePdfData(invoice.uuid);
            if (pdfDataResponse.success) {
              this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
                ...pdfDataResponse.body
              });
            } else {
              let msg = this.$i18n.t("An error occurred during PDF generation");
              if (pdfDataResponse.body && pdfDataResponse.body.non_field_errors) {
                msg = pdfDataResponse.body.non_field_errors
              }
              this.$errorDialog(msg);
              this.$evBus.$emit(ON_PDF_MODAL_CLOSE);
            }
          }
          this.hideDepositInvoiceModal();
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          } else {
            if (response.body && typeof response.body === 'object') {
              this.model.setMultipleErrors(response.body);
            } else {
              this.$errorDialog(this.$i18n.t("An error occurred during invoice generation."));
              this.getTableData();
            }
          }
        }
      },
      onSaleSelect(auction) {
        this.model.deposit_reservations[0].auction = auction
        this.model.deposit_reservations[0].sale_no = auction.sale_no
      },
      onCustomerSelect(customer) {
        this.model.customer = customer;
        this.customerUuid = customer.uuid
        this.setDefaultAddresses(customer.billing_address)
        this.model.preferred_language_code = customer.preferred_language_code;
        if (!this.model.uuid && hasPermission('change_customerbankaccount') && customer.term && this.hasStripeProvider) {
          this.model.exclude_from_auto_payments = customer.term.exclude_from_auto_payments;
        }
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "./scss/variables";

  .items-table {
    /deep/ .form-group {
      margin-top: .2em;
      margin-bottom: .5em;
    }
  }
</style>
